import type { RuntimeConfig } from './types.js';

const DEFAULTS = {
  branch: 'main',
  contentDir: 'content',
  imagesDir: 'images',
  authEndpoint: 'https://vectorswarmsolutions.com/api/vsscms',
  // Default GitHub OAuth App client ID for the vectorswarmsolutions-hosted
  // vsscms installation. Override via data-client-id on the script tag if
  // you're running your own OAuth App + auth function.
  clientId: 'Ov23ctBsW3s0wqRFLieM',
  editParam: 'edit',
} as const;

/**
 * Read configuration from the <script data-*> attributes of the tag that
 * loaded the runtime. Falls back to `document.currentScript` or the last
 * <script> with a `data-repo` attribute (handles `defer` and module loads).
 */
export function readConfig(): RuntimeConfig {
  const scriptEl = findScriptEl();
  if (!scriptEl) {
    throw new Error(
      '[vsscms] Could not locate the loading <script> tag. Make sure it has a data-repo attribute.'
    );
  }

  const repo = scriptEl.getAttribute('data-repo');
  if (!repo || !repo.includes('/')) {
    throw new Error(
      `[vsscms] data-repo must be set to "owner/name". Got: ${JSON.stringify(repo)}`
    );
  }

  const branch = scriptEl.getAttribute('data-branch') || DEFAULTS.branch;
  // Default content base is the current site origin (empty string — the
  // runtime turns this into a root-relative `/content/<page>.json` URL).
  //
  // Why: raw.githubusercontent.com 404s for private repos, which breaks
  // anonymous visitors on any client whose repo can't be made public.
  // The deploying host (Vercel/Netlify/etc.) already serves content/*.json
  // alongside the HTML at the same origin, so this is always available.
  //
  // The override via `data-content-base` is still honored — dev rigs use
  // it to point at a local Vite server, and any site that needs a CDN
  // or custom host can set it explicitly.
  const contentBase = scriptEl.getAttribute('data-content-base') || '';

  const cfg: RuntimeConfig = {
    repo,
    branch,
    contentDir: scriptEl.getAttribute('data-content-dir') || DEFAULTS.contentDir,
    imagesDir: scriptEl.getAttribute('data-images-dir') || DEFAULTS.imagesDir,
    contentBase,
    authEndpoint: scriptEl.getAttribute('data-auth-endpoint') || DEFAULTS.authEndpoint,
    clientId: scriptEl.getAttribute('data-client-id') || DEFAULTS.clientId,
    editParam: scriptEl.getAttribute('data-edit-param') || DEFAULTS.editParam,
    page: scriptEl.getAttribute('data-page') || derivePageFromUrl(location.pathname),
    scriptEl,
  };

  return cfg;
}

/**
 * Map a URL pathname to a page identifier used to find the content file.
 *
 * Examples:
 *   "/"              -> "index"
 *   "/index.html"    -> "index"
 *   "/about"         -> "about"
 *   "/about/"        -> "about"
 *   "/about.html"    -> "about"
 *   "/shop/rings"    -> "shop-rings"
 *   "/shop/rings/"   -> "shop-rings"
 */
export function derivePageFromUrl(pathname: string): string {
  // Strip leading and trailing slashes.
  let p = pathname.replace(/^\/+|\/+$/g, '');
  if (p === '' || p === 'index.html') return 'index';
  // Strip .html extension.
  p = p.replace(/\.html?$/i, '');
  // Slashes become dashes (nested pages).
  return p.replace(/\//g, '-');
}

function findScriptEl(): HTMLScriptElement | null {
  // `document.currentScript` is reliable for non-deferred, non-module scripts,
  // but null for defer/modules. Fall back to the last matching script tag.
  const current = document.currentScript;
  if (current && current instanceof HTMLScriptElement && current.hasAttribute('data-repo')) {
    return current;
  }
  const all = document.querySelectorAll<HTMLScriptElement>('script[data-repo]');
  return all.length > 0 ? all[all.length - 1] : null;
}
